import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { resilientFetch } from "@/lib/supabase/client";

export type AdminSession = {
  ok: boolean;
  configured: boolean;
  authenticated: boolean;
  adminLoginMethod?: "password" | "supabase" | "none";
  numerolojiStorage?: "supabase" | "memory";
};

const ADMIN_TOKEN_KEY = "admin_session_token";

function readAdminToken() {
  try {
    const v = window.localStorage.getItem(ADMIN_TOKEN_KEY);
    return typeof v === "string" ? v : "";
  } catch {
    return "";
  }
}

function saveAdminToken(token: string) {
  try {
    if (token) window.localStorage.setItem(ADMIN_TOKEN_KEY, token);
  } catch {
    // ignore
  }
}

function clearAdminToken() {
  try {
    window.localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    // ignore
  }
}


export function adminFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const token = readAdminToken();
  const headers = new Headers(init.headers || undefined);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return resilientFetch(input, { credentials: "include", ...init, headers });
}

/** For long-running requests (ElevenLabs, AI generation) — bypasses resilientFetch timeout/XHR fallback. */
export function adminFetchLong(input: RequestInfo | URL, init: RequestInit = {}) {
  const token = readAdminToken();
  const headers = new Headers(init.headers || undefined);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(input as RequestInfo, { credentials: "include", ...init, headers });
}

async function fetchSession(): Promise<AdminSession> {
  const res = await adminFetch("/api/admin/session");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Admin oturumu alınamadı.");
  }
  return json as AdminSession;
}

async function login(args: { email?: string; password: string }) {
  const res = await adminFetch("/api/admin/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Giriş başarısız.");
  }

  const token = typeof json?.token === "string" ? json.token : "";
  if (token) saveAdminToken(token);

  return json as { ok: true; token?: string };
}

async function logout() {
  const res = await adminFetch("/api/admin/logout", {
    method: "POST",
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Çıkış başarısız.");
  }
  clearAdminToken();
  return json as { ok: true };
}

export function useAdminSession() {
  const queryClient = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: ["admin-session"],
    queryFn: fetchSession,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const loginMutation = useMutation({
    mutationFn: (args: { email?: string; password: string }) => login(args),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-session"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-session"] });
    },
  });

  return {
    sessionQuery,
    loginMutation,
    logoutMutation,
  };
}
